import {
  useCreateBudgetaryMutation,
  usePlaceAnOrder,
  useSaveQuoteData,
} from "@/common/api/mutation";
import quotesRender from "@/common/const/elementsList/quotesRender.const";
import { useModal } from "@/common/contexts/Modal.context";
import { useGenerateFormElements } from "@/common/hooks";
import customerQuoteLineStrategy from "@/common/strategies/quoteLines/customer/customerQuoteLine.strategy";
import { FormFieldsStrategyType, QuotesType } from "@/common/types";
import { queryClient } from "@/common/utils";
import StayInPageButton from "@/components/ActionsButtons/StayInPageButton";
import {
  EnquireQuoteModal,
  InfoModal,
  WinPriceModal,
} from "@/components/Modals";
import Typography from "@/components/Typography/Typography";
import { Button, Form, Tag, message } from "antd";
import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { toast } from "react-toastify";

export default function QuoteLineForm({
  productType,
  quoteGeneralData,
  data,
  quoteLineId,
  quoteType,
}: {
  productType: QuotesType;
  data: any;
  quoteGeneralData: any;
  quoteLineId: number;
  quoteType?: "Firm" | "Budgetary";
}) {
  const [quoteLineFormStrategy, setQuoteLineFormStrategy] = useState<
    FormFieldsStrategyType[]
  >([]);
  const navigate = useNavigate();
  const { openModal, closeModal } = useModal();
  const [quoteLineForm] = Form.useForm();
  const [isTouched, setIsTouched] = useState(false);
  const quoteId = useParams().quoteId;
  const { mutate: saveQuote } = useSaveQuoteData();
  const { mutate: saveBudgetary } = useCreateBudgetaryMutation();
  const { mutate: placeOrder } = usePlaceAnOrder();

  useMemo(() => {
    if (!data) {
      return;
    }
    if (!productType) {
      return;
    }
    setQuoteLineFormStrategy(
      customerQuoteLineStrategy[productType].map((el) => {
        if (el.name === "targetOTC" || el.name === "targetMRC") {
          return {
            ...el,
            defaultValue: data[el.name]
              ? Number.parseFloat(data[el.name]).toFixed(2)
              : "0",
          };
        }
        if (el.name === "quoteType") {
          return { ...el, defaultValue: quoteType || "Budgetary" };
        }
        return {
          ...el,
          defaultValue: data[el.name] ? data[el.name].toString() : null,
        };
      })
    );
  }, [data]);
  const submitForm = () => {
    quoteLineForm.submit();
  };

  const onFinish = (values: any) => {
    return saveQuote(
      [
        {
          quoteReqId: quoteGeneralData.quoteId,
          quoteLines: [
            {
              quoteLineId: quoteLineId,
              vendorId: data.companyId,
              description: values.description || "",
            },
          ],
        },
      ],
      {
        onSuccess: () =>
          openModal(
            <InfoModal
              instruction={{
                behavior: "success",
                description: "",
                header: "Quote created",
              }}
              actions={[
                <StayInPageButton />,
                <Button
                  className="h-10 text-base font-inter font-semibold border-primary text-white"
                  type="primary"
                  onClick={() => {
                    closeModal();
                    navigate(-1);
                  }}
                >
                  Go to Quotes results
                </Button>,
              ]}
            />
          ),
      }
    );
  };

  const handlePlaceOrder = () => {
    if (!quoteId) return message.error("Error during order placing");
    saveQuote(
      [
        {
          quoteLines: [
            {
              quoteLineId,
              vendorId: data.companyId,
            },
          ],
          quoteReqId: Number(quoteId),
        },
      ],
      {
        onSuccess: (data: any) => {
          if (data.quotes.length === 0 || data.lines.length === 0)
            return message.error("Error during Order placing");
          placeOrder(
            {
              quoteId: data.quotes[0].id,
              quoteLineId: data.lines[0].id,
            },
            {
              onSuccess: () => {
                queryClient.invalidateQueries();
                toast.success("Order placed");
              },
            }
          );
        },
      }
    );
  };
  const requestFirmOffer = () => {
    saveBudgetary(
      {
        quoteLineId: quoteLineId,
        vendorId: data?.companyId || 0,
        quoteReqId: quoteId || 0,
      },
      {
        onSuccess: () => {
          return toast.success("Request sent");
        },
      }
    );
  };

  const { formElements } = useGenerateFormElements(quoteLineFormStrategy);
  const pricing = [...formElements.slice(0, 4)];
  const productData = [...formElements.slice(4, -1)];
  return (
    <div>
      <div>
        <Form
          onFinish={onFinish}
          form={quoteLineForm}
          onValuesChange={() => {
            setIsTouched(true);
          }}
        >
          <div className="bg-white px-5 rounded-lg py-5">
            <div className="flex gap-2 items-center ">
              <Typography
                mb={4}
                type="p"
                fontFamily="font-kanit"
                fontWeight="font-medium"
                textColor="black"
                customStyles="pt-1"
              >
                {quotesRender.find((el) => el.value === productType)?.label}
              </Typography>
              <Tag className="text-primary font-kanit text-[13px] leading-[13px] flex items-center justify-center border-none bg-primaryDim py-2">
                Pricing
              </Tag>
            </div>
            <div className="grid grid-cols-4 gap-4">{...pricing}</div>
          </div>
          <div className="grid grid-cols-4 gap-4 bg-white p-5 mt-5 rounded-lg">
            {...productData}
          </div>

          <div className="w-full hidden mt-4">
            {formElements[formElements.length - 1]}
          </div>
        </Form>
        {isTouched && (
          <Button
            type="text"
            className="text-primary font-inter font-semibold text-sm"
            onClick={() => {
              quoteLineForm.resetFields();
              setIsTouched(false);
            }}
          >
            Reset changed values
          </Button>
        )}
      </div>

      <div className="flex items-center justify-between">
        <div className="flex gap-4 mt-5 mb-5">
          <Button
            onClick={() => {
              openModal(
                <EnquireQuoteModal
                  data={{
                    quoteReqId: quoteGeneralData.quoteId,
                    price: { companyId: data.companyId },
                    id: quoteLineId,
                  }}
                />
              );
            }}
            type="primary"
            className="h-10 text-base font-inter font-semibold bg-blue-light text-primary"
          >
            Enquire
          </Button>
          <Button
            onClick={() => {
              openModal(
                <WinPriceModal
                  data={{
                    quoteReqId: quoteGeneralData.quoteId,
                    price: { companyId: data.companyId },
                    id: quoteLineId,
                  }}
                />
              );
            }}
            type="primary"
            className="h-10 text-base font-inter font-semibold bg-blue-light text-primary"
          >
            Win price
          </Button>
          {quoteType === "Budgetary" && (
            <Button
              onClick={requestFirmOffer}
              type="primary"
              className="h-10 text-base font-inter font-semibold bg-blue-light text-primary"
            >
              Request Firm Offer
            </Button>
          )}
          <Button
            onClick={submitForm}
            type="primary"
            className="h-10 text-base font-inter font-semibold"
          >
            Save Quote
          </Button>
        </div>

        {quoteType === "Firm" && (
          <Button
            onClick={handlePlaceOrder}
            type="primary"
            className="h-10 text-base font-inter font-semibold bg-blue-light text-primary"
          >
            Place an order
          </Button>
        )}
      </div>
    </div>
  );
}
